import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './BaasHigherSavingsContent.scss';

const BaasHigherSavingsContent = (props) => {
  const location = useLocation();
  const isOrbiter = location.pathname.toLowerCase().includes('orbiter-baas');

  const buttonText = isOrbiter ? 'Reserve Now' : 'Book Now';
  const buttonUrl = isOrbiter
    ? '/electric-scooters/tvs-orbiter/online-booking'
    : '/electric-scooters/tvs-iqube/online-booking';

  const [variants, setVariants] = useState([]);
  const [states, setStates] = useState([]);

  const [variantId, setVariantId] = useState(null);
  const [stateId, setStateId] = useState(null);

  const [tenureData, setTenureData] = useState([]);
  const [apiResponse, setApiResponse] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const scooterSlug = pathname.split('/')[2] || 'evscooter';

  const BAAS_API = `/electric-scooters/${scooterSlug}/api/VehicleFeatures/ProductFeatures/BaaSPricing`;

  // ================= GET =================
  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const res = await fetch(BAAS_API);
      const data = await res.json();
      const api = data?.apiResponse;

      const statesList = api?.CityPriceDto?.States || [];
      const variantsList = api?.CityPriceDto?.VariantList || [];

      setStates(statesList);
      setVariants(variantsList);

      const defaultState = statesList.find((s) => s.IsDefault) || statesList[0];
      const defaultVariant = variantsList.find((v) => v.IsDefault) || variantsList[0];

      if (defaultState) setStateId(Number(defaultState.Id));
      if (defaultVariant) setVariantId(Number(defaultVariant.Id));

      setIsInitialized(true);
    } catch (err) {
      console.error('GET API error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ================= POST =================
  const fetchUpdatedPricing = async (vId, sId) => {
    try {
      setLoading(true);

      const res = await fetch(BAAS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variantId: vId,
          stateId: sId,
        }),
      });

      const data = await res.json();

      setApiResponse(data?.apiResponse || null);
      setTenureData(data?.apiResponse?.BaaSPriceDetailsList || []);
    } catch (err) {
      console.error('POST API error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!isInitialized || !variantId || !stateId) return;
    fetchUpdatedPricing(variantId, stateId);
  }, [variantId, stateId, isInitialized]);

  // ================= VALIDATION =================
  const priceData = apiResponse?.CityPriceDto?.DefaultCityPriceEmi?.Price;

  const isTableValid =
    priceData !== null &&
    priceData !== undefined &&
    priceData?.BaaSPrice !== null &&
    priceData?.BaaSPrice !== undefined;

  if (loading && !tenureData.length) {
    return <div>Loading pricing...</div>;
  }

  return (
    <section className="higher-section">
      <div className="baas-higher-savings-content-container">
        <h1 className="title">{props?.fields?.Title?.value}</h1>
        <p className="subtitle">{props?.fields?.SubTitle?.value}</p>

        {/* ================= SELECTORS ================= */}
        <div className="variant-flex">
          {/* VARIANT */}
          <div className={`floating-field ${variantId ? 'active' : ''}`}>
            {variants.length > 0 && (
              <select
                value={variantId ? String(variantId) : ''}
                onChange={(e) => setVariantId(Number(e.target.value))}
              >
                {variants.map((v) => (
                  <option key={v.Id} value={String(v.Id)}>
                    {v.VariantName}
                  </option>
                ))}
              </select>
            )}
            <label>{props?.fields?.SelectVariant?.value}</label>
          </div>

          {/* STATE */}
          <div className={`floating-field ${stateId ? 'active' : ''}`}>
            {states.length > 0 && (
              <select
                value={stateId ? String(stateId) : ''}
                onChange={(e) => setStateId(Number(e.target.value))}
              >
                {states.map((s) => (
                  <option key={s.Id} value={String(s.Id)}>
                    {s.Name}
                  </option>
                ))}
              </select>
            )}
            <label>{props?.fields?.SelectState?.value}</label>
          </div>
        </div>

        {/* ================= TABLE / MESSAGE ================= */}
        {isTableValid ? (
          <div className="higher-container">
            <div className="higher-content-inner">
              <div className="higher-list">
                <div className="higher-list-line">
                  <table className="pricing-table">
                    <tbody>
                      <tr>
                        <th></th>
                        {tenureData.map((t, i) => (
                          <th key={i}>{t.Tenure}</th>
                        ))}
                      </tr>

                      <tr className="highlight">
                        <td>{props?.fields?.ExShroom?.value}</td>
                        {tenureData.map((t, i) => (
                          <td key={i}>{t.EffectiveExShowroomWithBaaS}</td>
                        ))}
                      </tr>

                      <tr className="highlight-m">
                        <td>{props?.fields?.Monthly?.value}</td>
                        {tenureData.map((t, i) => (
                          <td key={i}>{t.MonthlyPaymentCost}</td>
                        ))}
                      </tr>

                      <tr>
                        <td>{props?.fields?.Battery?.value}</td>
                        {tenureData.map((t, i) => (
                          <td key={i}>{t.BatteryWarrentyCoverage}</td>
                        ))}
                      </tr>

                      <tr>
                        <td>{props?.fields?.KMs?.value}</td>
                        {tenureData.map((_, i) => (
                          <td key={i}>Unlimited</td>
                        ))}
                      </tr>

                      <tr className="highlight-m bg">
                        <td>
                          {props?.fields?.EVOwnership?.value}
                          <p>
                            1st year of ownership savings, inclusive of running cost & vehicle price
                          </p>
                        </td>
                        {tenureData.map((t, i) => (
                          <td key={i}>{t.EvOwnershipSavings}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  {/* MOBILE */}
                  {/* MOBILE */}
                  <div className="slider">
                    {tenureData.map((t, i) => (
                      <div className="card" key={i}>
                        <h3>Tenure {t.Tenure}</h3>

                        <div className="price">
                          <span>{props?.fields?.ExShroom?.value}</span>
                          <span>{t.EffectiveExShowroomWithBaaS}</span>
                        </div>

                        <div className="payment-cost">
                          <span>{props?.fields?.Monthly?.value}</span>
                          <span>{t.MonthlyPaymentCost}</span>
                        </div>

                        <div className="payment-cost-n">
                          <span>{props?.fields?.Battery?.value}</span>
                          <span>{t.BatteryWarrentyCoverage}</span>
                        </div>

                        <div className="payment-cost-n">
                          <span>{props?.fields?.KMs?.value}</span>
                          <span>Unlimited</span>
                        </div>

                        <div className="payment-cost">
                          <span>
                            {props?.fields?.EVOwnership?.value}
                            <p>
                              1st year of ownership savings, inclusive of running cost & vehicle
                              price
                            </p>
                          </span>
                          <span>{t.EvOwnershipSavings}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          !loading && (
            <div
              style={{
                textAlign: 'center',
                padding: '30px',
                color: '#666',
              }}
            >
              Pricing is not available for the selected variant and state.
            </div>
          )
        )}

        {/* ================= CTA ================= */}
        <div className="disclaimer">
          <p>{props?.fields?.Disclaimer?.value}</p>
          <a href={buttonUrl} className="btn btn-primary">
            {buttonText}
          </a>
        </div>
      </div>
    </section>
  );
};

export default React.memo(BaasHigherSavingsContent);
